import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { Button, Col, Empty, Row, Tabs } from "antd";
import { blue, cyan } from "@ant-design/colors";

import { Bar, Line } from "@ant-design/charts";
import { ReloadOutlined } from "@ant-design/icons";
import LoadingPage from "../../components/LoadingPage";
import { chain, sumBy, isEmpty } from "lodash";

import Moment from "moment";
import { extendMoment } from "moment-range";

import {
  getRobotWeldHoursList,
  getRobotWeldInchesList,
  getRobotOperatorsWeldHoursList,
  getRobotOperatorsWeldInchesList,
} from "../../features/robot/robotSlice";

import { dataOffsetDate, GetDateRange } from "../../libs/utils";

import PickerWithType from "../../libs/components/PickerWithType";
import SelectDateType from "../../libs/components/SelectDateType";

const moment = extendMoment(Moment);

const { TabPane } = Tabs;

const filterData = [
  {
    key: 1,
    title: "Last 24 hours",
    value: 1,
    date: moment().subtract(1, "days"),
  },
  {
    key: 2,
    title: "Last 7 days",
    value: 7,
    date: moment().subtract(7, "days"),
  },
  {
    key: 3,
    title: "Last 30 days",
    value: 30,
    date: moment().subtract(30, "days"),
  },
];

const activeButtonStyle = {
  color: blue[4], //#40a9ff
  borderColor: blue[4], //#40a9ff
};

const RobotPerformanceDetail = (props) => {
  const dispatch = useDispatch();
  const [filterButtonKey, setFilterButtonKey] = useState(3); // default view as last 30 days

  const ref = useRef();

  const { robotName } = useParams();
  const robotId =
    robotName && robotName.match(/(\d+)/) && robotName.match(/(\d+)/)[0];

  const weld_hours = useSelector((state) => state.robot.weldHours);
  const weld_inches = useSelector((state) => state.robot.weldInches);

  const weld_hours_operators = useSelector(
    (state) => state.robot.weldHoursOperators
  );
  const weld_inches_operators = useSelector(
    (state) => state.robot.weldInchesOperators
  );

  // TODO: this can be refactored into map function however it does not render with map currently

  const [dateValues, setDateValues] = useState(null);
  const [selectedValues, setSelectedValues] = useState(null);
  const [type, setType] = useState("range");

  const today = moment();
  const lastWeek = moment().subtract(7, "days");

  // const today = moment().format("YYYY-MM-DD");
  const start = moment().subtract(1, "month").format("YYYY-MM-DD");

  const { robot, showOnlyPane } = props;

  useEffect(() => {
    if (showOnlyPane === "1") {
      dispatch(
        getRobotWeldHoursList(robotId, {
          start: lastWeek.format("YYYY-MM-DD"),
          end: today.format("YYYY-MM-DD"),
        })
      );
    }
    if (showOnlyPane === "2") {
      dispatch(
        getRobotWeldInchesList(robotId, {
          start: lastWeek.format("YYYY-MM-DD"),
          end: today.format("YYYY-MM-DD"),
        })
      );
    }
    if (showOnlyPane === "3") {
      dispatch(
        getRobotOperatorsWeldHoursList(robotId, {
          start: lastWeek.format("YYYY-MM-DD"),
          end: today.format("YYYY-MM-DD"),
        })
      );
      dispatch(
        getRobotOperatorsWeldInchesList(robotId, {
          start: lastWeek.format("YYYY-MM-DD"),
          end: today.format("YYYY-MM-DD"),
        })
      );
    }
  }, [dispatch]);

  if (
    showOnlyPane === "1" &&
    (!weld_hours || !(weld_hours && weld_hours[robotId]))
  ) {
    return <LoadingPage />;
  }
  if (
    showOnlyPane === "2" &&
    (!weld_inches || !(weld_inches && weld_inches[robotId]))
  ) {
    return <LoadingPage />;
  }

  if (
    showOnlyPane === "3" &&
    (!weld_hours_operators ||
      !(weld_hours_operators && weld_hours_operators[robotId]))
  ) {
    return <LoadingPage />;
  }
  if (
    showOnlyPane === "3" &&
    (!weld_inches_operators ||
      !(weld_inches_operators && weld_inches_operators[robotId]))
  ) {
    return <LoadingPage />;
  }

  const customOperations = (
    <>
      <SelectDateType value={type} onChange={setType}></SelectDateType>
      <PickerWithType
        type={type}
        defaultValue={[lastWeek, today]}
        onChange={(value) => setDateValues(value)}
      />
      <Button
        disabled={!dateValues}
        onClick={() => {
          const dateRange = GetDateRange(type, dateValues);

          switch (showOnlyPane) {
            case "1":
              dispatch(
                getRobotWeldHoursList(robotId, {
                  start: dateRange[0],
                  end: dateRange[1],
                })
              );
              return;
            case "2":
              dispatch(
                getRobotWeldInchesList(robotId, {
                  start: dateRange[0],
                  end: dateRange[1],
                })
              );
              return;
            case "3":
              dispatch(
                getRobotOperatorsWeldHoursList(robotId, {
                  start: dateRange[0],
                  end: dateRange[1],
                })
              );
              dispatch(
                getRobotOperatorsWeldInchesList(robotId, {
                  start: dateRange[0],
                  end: dateRange[1],
                })
              );
              return;
            default:
              return;
          }
        }}
        icon={<ReloadOutlined />}
      ></Button>
    </>
  );

  let range = Array.from(
    moment
      .range(lastWeek.format("YYYY-MM-DD"), today.format("YYYY-MM-DD"))
      .by("day")
  );

  if (dateValues) {
    const dateRange = GetDateRange(type, dateValues);
    range = Array.from(moment.range(dateRange[0], dateRange[1]).by("day"));
  }

  return (
    <>
      <Tabs
        tabBarExtraContent={customOperations}
        onTabClick={(key) => {
          // Performance per Operator is only for last 7 days
          if (key === 3) setFilterButtonKey(2);
        }}
        defaultActiveKey={showOnlyPane}
      >
        {(!showOnlyPane || showOnlyPane === "1") && (
          <TabPane tab={"Weld Hours"} key="1" style={{ height: 407 }}>
            <Line
              {...{
                data: range.map((item) => {
                  const date = item.format("YYYY-MM-DD");
                  const weldDoneDate = weld_hours[robotId]
                    .map((item) => ({
                      ...item,
                      weld_time: moment.duration(item.value).asMinutes(),
                    }))
                    .filter((item) => item.date === date);

                  return {
                    date: date,
                    weld_time:
                      (weldDoneDate &&
                        weldDoneDate.length > 0 &&
                        weldDoneDate[0].weld_time) ||
                      0,
                  };
                }),

                xField: "date",
                yField: "weld_time",
                xAxis: {
                  label: {
                    formatter: function formatter(v) {
                      return moment(v).format("MMM-DD");
                    },
                  },
                },
                yAxis: {
                  label: {
                    formatter: function formatter(v) {
                      return (
                        moment.duration({ minutes: v }).asMinutes().toFixed(0) +
                        " min"
                      );
                    },
                  },
                },
                tooltip: {
                  formatter: (datum) => {
                    return {
                      name: datum.date,
                      value:
                        moment
                          .duration({ minutes: datum.weld_time })
                          .asMinutes()
                          .toFixed(1) + " min",
                    };
                  },
                },
              }}
              chartRef={ref}
            />
          </TabPane>
        )}
        {(!showOnlyPane || showOnlyPane === "2") && (
          <TabPane tab={"Weld Inches"} key="2" style={{ height: 407 }}>
            <Line
              {...{
                data: range.map((item) => {
                  const date = item.format("YYYY-MM-DD");
                  const weldDoneDate = weld_inches[robotId].filter(
                    (item) => item.date === date
                  );

                  return {
                    date: date,
                    value:
                      (weldDoneDate &&
                        weldDoneDate.length > 0 &&
                        weldDoneDate[0].value) ||
                      0,
                  };
                }),

                xField: "date",
                yField: "value",
                xAxis: {
                  label: {
                    formatter: function formatter(v) {
                      return moment(v).format("MMM-DD");
                    },
                  },
                },
                yAxis: {
                  label: {
                    formatter: function formatter(v) {
                      return v + "″";
                    },
                  },
                },
                tooltip: {
                  formatter: (datum) => {
                    return {
                      name: datum.date,
                      value: datum.value + "″",
                    };
                  },
                },
              }}
              chartRef={ref}
            />
          </TabPane>
        )}
        {(!showOnlyPane || showOnlyPane === "3") && (
          <TabPane tab={"Operator"} key="3" style={{ height: 407 }}>
            <>
              <Row>
                <Col span={24} style={{ paddingBottom: 20 }}>
                  <>
                    <b>Top Operators by Weld Hours</b>
                    <div>
                      <Bar
                        {...{
                          data: [
                            ...weld_hours_operators[robotId],
                            {
                              weld_hours: weld_hours_operators[robotId]
                                .slice(1)
                                .reduce((prev, curr) => {
                                  return prev.add(curr.weld_hours);
                                }, moment.duration((weld_hours_operators[robotId][0] && weld_hours_operators[robotId][0].weld_hours) || 0))
                                ._data,

                              operator_name: "Overall",
                            },
                          ].map((item) => ({
                            ...item,
                            weld_time: moment
                              .duration(item.weld_hours)
                              .asMinutes(),
                          })),
                          xField: "weld_time",
                          yField: "operator_name",
                          seriesField: "operator_name",
                          color: ({ operator_name }) => {
                            return operator_name === "Overall"
                              ? blue[7] //#0050b3
                              : blue[5]; //#1890ff
                          },

                          legend: false,
                          height: 150,
                          barWidthRatio: 0.9,
                          maxBarWidth: 20,
                          xAxis: {
                            label: {
                              formatter: function formatter(v) {
                                return (
                                  moment
                                    .duration({ minutes: v })
                                    .asHours()
                                    .toFixed(1) + "hr"
                                );
                              },
                            },
                          },
                          tooltip: {
                            formatter: (datum) => {
                              return {
                                name: datum.operator_name,
                                value:
                                  datum.weld_time &&
                                  moment
                                    .duration({ minute: datum.weld_time })
                                    .asHours()
                                    .toFixed(1) + " hours",
                              };
                            },
                          },
                        }}
                        chartRef={ref}
                      />
                    </div>
                  </>
                </Col>
                <Col span={24}>
                  <>
                    <b>Top Operators by Weld Inches (FDI)</b>
                    <div>
                      <Bar
                        {...{
                          data: [
                            ...weld_inches_operators[robotId],
                            {
                              value: weld_inches_operators[robotId].reduce(
                                (prev, curr) => prev + curr.value,
                                0
                              ),
                              operator_name: "Overall",
                            },
                          ],
                          xField: "value",
                          yField: "operator_name",
                          seriesField: "operator_name",
                          color: ({ operator_name }) => {
                            return operator_name === "Overall"
                              ? cyan[7] //#006d75
                              : cyan[5]; //#13c2c2
                          },
                          legend: false,
                          height: 150,
                          barWidthRatio: 0.9,
                          maxBarWidth: 20,
                          xAxis: {
                            label: {
                              formatter: function formatter(v) {
                                return v + "″";
                              },
                            },
                          },
                          tooltip: {
                            formatter: (datum) => {
                              return {
                                name: datum.operator_name,
                                value:
                                  datum.value &&
                                  datum.value.toFixed(1) + " inches",
                              };
                            },
                          },
                        }}
                        chartRef={ref}
                      />
                    </div>
                  </>
                </Col>
              </Row>
            </>
          </TabPane>
        )}
      </Tabs>
    </>
  );
};

export default RobotPerformanceDetail;
